import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import axiosInstance from "../../utils/api";
import { FiLogIn, FiLogOut, FiCheckCircle, FiXCircle, FiRotateCcw, FiCamera } from "react-icons/fi";

const Scan = () => {
  const qrCodeRef = useRef(null);
  const hasScanned = useRef(false);

  const [mode, setMode] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!mode) return;

    const startScanner = async () => {
      try {
        hasScanned.current = false;
        setMessage(null);
        setError(null);

        qrCodeRef.current = new Html5Qrcode("qr-reader");

        await qrCodeRef.current.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 250 },
          async (decodedText) => {
            if (hasScanned.current) return;

            hasScanned.current = true; 

            try {
              await axiosInstance.post("/badge/scan", {
                badgeId: decodedText,
                action: mode,
              });

              setMessage(`${mode.toUpperCase()} successful`);
            } catch (err) {
              setError(
                err.response?.data?.message || "Scan failed"
              );
            } finally {
              stopScanner();
            }
          }
        );
      } catch (err) {
        console.error("Scanner error:", err);
      }
    };

    startScanner();

    return () => stopScanner();
  }, [mode]);

  const stopScanner = async () => {
    try {
      if (qrCodeRef.current) {
        await qrCodeRef.current.stop();
        await qrCodeRef.current.clear();
        qrCodeRef.current = null;
      }
    } catch (err) {
      console.warn("Stop skipped:", err.message);
    }
  };

  const resetScan = () => {
    setMode(null);
    setMessage(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <FiCamera className="text-white" size={24} />
            <h2 className="text-xl font-bold text-white">
              Badge Scanner
            </h2>
          </div>
          <p className="text-blue-100 text-sm">Security checkpoint access control</p>
        </div>

        <div className="p-6">
          {/* MODE SELECTION */}
          {!mode && (
            <div className="space-y-3">
              <p className="text-center text-sm text-slate-600 mb-4 font-medium">
                Select scan mode to begin
              </p>
              <button
                onClick={() => setMode("entry")}
                className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-xl font-semibold bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <FiLogIn size={20} />
                <span>ENTRY SCAN</span>
              </button>

              <button
                onClick={() => setMode("exit")}
                className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-xl font-semibold bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <FiLogOut size={20} />
                <span>EXIT SCAN</span>
              </button>
            </div>
          )}

          {/* CAMERA PREVIEW */}
          {mode && !message && !error && (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-slate-100 to-slate-50 rounded-lg p-3 border border-slate-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 font-medium">Scan Mode:</span>
                  <span className={`px-3 py-1 rounded-full font-semibold text-white ${
                    mode === 'entry' 
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                      : 'bg-gradient-to-r from-orange-500 to-red-600'
                  }`}>
                    {mode === 'entry' ? <FiLogIn className="inline mr-1" size={14} /> : <FiLogOut className="inline mr-1" size={14} />}
                    {mode.toUpperCase()}
                  </span>
                </div>
              </div>
              
              <div
                id="qr-reader"
                className="w-full rounded-xl overflow-hidden border-4 border-slate-200 shadow-inner"
              />
              
              <p className="text-center text-xs text-slate-500 italic">
                Position the QR code within the frame
              </p>

              <button
                onClick={resetScan}
                className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2.5 px-4 rounded-lg font-medium transition-all border border-slate-300"
              >
                <FiRotateCcw size={16} />
                Cancel & Change Mode
              </button>
            </div>
          )}

          {/* SUCCESS MESSAGE */}
          {message && (
            <div className="space-y-4 text-center">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
                <FiCheckCircle className="text-green-600 mx-auto mb-3" size={48} />
                <p className="text-green-700 font-semibold text-lg mb-1">
                  {message}
                </p>
                <p className="text-green-600 text-sm">
                  Badge verified and logged
                </p>
              </div>
              <button
                onClick={resetScan}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-slate-700 to-slate-900 text-white py-3 px-6 rounded-xl font-medium hover:from-slate-800 hover:to-black transition-all shadow-md hover:shadow-lg"
              >
                <FiRotateCcw size={18} />
                Scan Another Badge
              </button>
            </div>
          )}

          {/* ERROR MESSAGE */}
          {error && (
            <div className="space-y-4 text-center">
              <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-6 border-2 border-red-200">
                <FiXCircle className="text-red-600 mx-auto mb-3" size={48} />
                <p className="text-red-700 font-semibold text-lg mb-1">
                  Scan Failed
                </p>
                <p className="text-red-600 text-sm">
                  {error}
                </p>
              </div>
              <button
                onClick={resetScan}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-slate-700 to-slate-900 text-white py-3 px-6 rounded-xl font-medium hover:from-slate-800 hover:to-black transition-all shadow-md hover:shadow-lg"
              >
                <FiRotateCcw size={18} />
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Scan;